/**
 * Страница трекинга обострений (рецидивов).
 * Позволяет пациенту создавать, редактировать и удалять записи об обострениях.
 */

import React, { useEffect, useState } from 'react';
import { relapseAPI } from '../../api/client';
import { Relapse } from '../../types';

const SEVERITY_OPTIONS = [
  { value: 'mild', label: 'Лёгкое', color: '#f59e0b' },
  { value: 'moderate', label: 'Умеренное', color: '#f97316' },
  { value: 'severe', label: 'Тяжёлое', color: '#dc2626' },
];

const FUNCTIONAL_SYSTEMS = [
  'Пирамидная', 'Мозжечковая', 'Стволовая', 'Чувствительная',
  'Тазовые функции', 'Зрительная', 'Церебральная', 'Другое',
];

const RelapsesPage: React.FC = () => {
  const [relapses, setRelapses] = useState<Relapse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  // Форма
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [severity, setSeverity] = useState('moderate');
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);
  const [treatment, setTreatment] = useState('');
  const [notes, setNotes] = useState('');

  const loadRelapses = async () => {
    try {
      const res = await relapseAPI.getList({ limit: 50 });
      setRelapses(res.data.relapses);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Ошибка загрузки обострений:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRelapses();
  }, []);

  const resetForm = () => {
    setStartDate('');
    setEndDate('');
    setSeverity('moderate');
    setSelectedSystems([]);
    setTreatment('');
    setNotes('');
    setEditId(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!startDate || !severity) return;

    const data = {
      start_date: startDate,
      end_date: endDate || undefined,
      severity,
      affected_systems: selectedSystems.length > 0 ? JSON.stringify(selectedSystems) : undefined,
      treatment: treatment || undefined,
      notes: notes || undefined,
    };

    try {
      if (editId) {
        await relapseAPI.update(editId, data);
      } else {
        await relapseAPI.create(data);
      }
      resetForm();
      loadRelapses();
    } catch (err) {
      console.error('Ошибка сохранения:', err);
    }
  };

  const handleEdit = (r: Relapse) => {
    setEditId(r.id);
    setStartDate(r.start_date);
    setEndDate(r.end_date || '');
    setSeverity(r.severity);
    try {
      setSelectedSystems(r.affected_systems ? JSON.parse(r.affected_systems) : []);
    } catch {
      setSelectedSystems([]);
    }
    setTreatment(r.treatment || '');
    setNotes(r.notes || '');
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить запись об обострении?')) return;
    try {
      await relapseAPI.delete(id);
      loadRelapses();
    } catch (err) {
      console.error('Ошибка удаления:', err);
    }
  };

  const toggleSystem = (sys: string) => {
    setSelectedSystems(prev =>
      prev.includes(sys) ? prev.filter(s => s !== sys) : [...prev, sys]
    );
  };

  const getSeverityInfo = (sev: string) =>
    SEVERITY_OPTIONS.find(s => s.value === sev) || SEVERITY_OPTIONS[1];

  if (loading) return <div style={{ padding: 40 }}>Загрузка...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1>Обострения</h1>
        <button style={styles.primaryBtn} onClick={() => { resetForm(); setShowForm(!showForm); }}>
          {showForm ? 'Отмена' : 'Новое обострение'}
        </button>
      </div>

      {/* Форма */}
      {showForm && (
        <div style={styles.formCard}>
          <h3 style={{ marginBottom: 16 }}>{editId ? 'Редактирование' : 'Новое обострение'}</h3>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Дата начала *</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={styles.input} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Дата окончания</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={styles.input} />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Тяжесть *</label>
            <div style={{ display: 'flex', gap: 12 }}>
              {SEVERITY_OPTIONS.map(opt => (
                <label key={opt.value} style={{
                  ...styles.radioLabel,
                  backgroundColor: severity === opt.value ? opt.color : 'var(--bg-card)',
                  color: severity === opt.value ? '#fff' : 'var(--text-primary)',
                  border: `2px solid ${opt.color}`,
                }}>
                  <input
                    type="radio" name="severity" value={opt.value}
                    checked={severity === opt.value}
                    onChange={() => setSeverity(opt.value)}
                    style={{ display: 'none' }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Поражённые системы</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {FUNCTIONAL_SYSTEMS.map(sys => (
                <label key={sys} style={{
                  ...styles.checkboxLabel,
                  backgroundColor: selectedSystems.includes(sys) ? '#2563eb' : 'var(--bg-card)',
                  color: selectedSystems.includes(sys) ? '#fff' : 'var(--text-primary)',
                }}>
                  <input
                    type="checkbox" checked={selectedSystems.includes(sys)}
                    onChange={() => toggleSystem(sys)}
                    style={{ display: 'none' }}
                  />
                  {sys}
                </label>
              ))}
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Лечение</label>
            <textarea value={treatment} onChange={e => setTreatment(e.target.value)}
              placeholder="Метилпреднизолон, плазмаферез..."
              style={{ ...styles.input, minHeight: 60, resize: 'vertical' }} />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Заметки</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Описание симптомов, наблюдения..."
              style={{ ...styles.input, minHeight: 60, resize: 'vertical' }} />
          </div>

          <button style={styles.primaryBtn} onClick={handleSubmit}>
            {editId ? 'Сохранить изменения' : 'Создать запись'}
          </button>
        </div>
      )}

      {/* Список обострений */}
      {relapses.length === 0 ? (
        <div style={styles.emptyState}>
          <p>Записей об обострениях пока нет.</p>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Отслеживание обострений помогает врачу оценить течение заболевания.
          </p>
        </div>
      ) : (
        <div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
            Всего записей: {total}
          </p>
          {relapses.map(r => {
            const sevInfo = getSeverityInfo(r.severity);
            const isActive = !r.end_date;
            let systems: string[] = [];
            try { systems = r.affected_systems ? JSON.parse(r.affected_systems) : []; } catch {}

            return (
              <div key={r.id} style={{
                ...styles.relapseCard,
                borderLeft: `4px solid ${sevInfo.color}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <strong>{r.start_date}</strong>
                      {r.end_date && <span style={{ color: 'var(--text-secondary)' }}> — {r.end_date}</span>}
                      <span style={{
                        ...styles.severityBadge,
                        backgroundColor: sevInfo.color,
                      }}>
                        {sevInfo.label}
                      </span>
                      {isActive && (
                        <span style={styles.activeBadge}>Активное</span>
                      )}
                    </div>
                    {systems.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
                        {systems.map(s => (
                          <span key={s} style={styles.systemBadge}>{s}</span>
                        ))}
                      </div>
                    )}
                    {r.treatment && <p style={{ fontSize: 13, margin: '4px 0' }}>Лечение: {r.treatment}</p>}
                    {r.notes && <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0' }}>{r.notes}</p>}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={styles.smallBtn} onClick={() => handleEdit(r)}>Ред.</button>
                    <button style={{ ...styles.smallBtn, color: '#dc2626' }} onClick={() => handleDelete(r.id)}>X</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  primaryBtn: {
    backgroundColor: '#2563eb', color: '#fff', border: 'none',
    padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600,
  },
  formCard: {
    backgroundColor: 'var(--bg-card)', padding: 24, borderRadius: 12,
    boxShadow: 'var(--shadow-card)', marginBottom: 24,
  },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  formGroup: { marginBottom: 16 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' },
  input: {
    width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)',
    borderRadius: 8, fontSize: 14, backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)',
  },
  radioLabel: {
    padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
    fontSize: 14, fontWeight: 500, transition: 'all 0.2s',
  },
  checkboxLabel: {
    padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
    fontSize: 13, border: '1px solid var(--border-color)', transition: 'all 0.2s',
  },
  relapseCard: {
    backgroundColor: 'var(--bg-card)', padding: 16, borderRadius: 12,
    boxShadow: 'var(--shadow-card)', marginBottom: 10,
  },
  severityBadge: {
    color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
  },
  activeBadge: {
    backgroundColor: '#dc2626', color: '#fff', padding: '2px 10px',
    borderRadius: 12, fontSize: 11, fontWeight: 600, animation: 'pulse 2s infinite',
  },
  systemBadge: {
    backgroundColor: 'var(--bg-table-header)', padding: '2px 8px',
    borderRadius: 6, fontSize: 11, color: 'var(--text-secondary)',
  },
  smallBtn: {
    background: 'var(--bg-card)', border: '1px solid var(--border-color)',
    padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
    color: 'var(--text-primary)',
  },
  emptyState: {
    backgroundColor: 'var(--bg-card)', padding: 40, borderRadius: 12,
    boxShadow: 'var(--shadow-card)', textAlign: 'center',
  },
};

export default RelapsesPage;
